import { useQuery } from '@tanstack/react-query'
import { createFileRoute } from '@tanstack/react-router'
import { ChartColumn, ChevronLeft, ChevronRight } from 'lucide-react'
import { useState, type ReactNode } from 'react'
import { classTypesQuery, earningsQuery, type Earnings, type Figures } from '@/api/services'
import { ColumnChart } from '@/app/access/charts'
import { AccrualsPanel, type DrillOpen } from '@/app/services/accruals-panel'
import { BranchSelect, ServicesShell, type ServicesContext } from '@/app/services/services-shell'
import { wallOf } from '@/domain/calendar'
import { errorText } from '@/domain/errors'
import { formatMoney } from '@/domain/pass-rules'
import { addMonths, compactMoney, daysOfMonth, monthLabel, monthOf } from '@/domain/services'
import { formatDate, t } from '@/i18n'
import { cn } from '@/lib/cn'
import { IconButton } from '@/ui/button'
import { DataList, type DataColumn } from '@/ui/data-list'
import { InlineBanner } from '@/ui/inline-banner'
import { EmptyState } from '@/ui/page'
import { SectionHeader } from '@/ui/section-header'
import { LoadingBlock } from '@/ui/spinner'
import { StatTile } from '@/ui/stat-tile'

type Search = { month?: string; branch?: string }

export const Route = createFileRoute('/_app/c/$orgId/services/analytics')({
  validateSearch: (search: Record<string, unknown>): Search => ({
    ...(typeof search['month'] === 'string' && /^\d{4}-(0[1-9]|1[0-2])$/.test(search['month']) ? { month: search['month'] } : {}),
    ...(typeof search['branch'] === 'string' && /^[0-9a-f-]{36}$/.test(search['branch']) ? { branch: search['branch'] } : {}),
  }),
  component: Analytics,
})

function Analytics() {
  const { orgId } = Route.useParams()
  return (
    <ServicesShell orgId={orgId} tab="analytics">
      {(ctx) => <AnalyticsScreen ctx={ctx} />}
    </ServicesShell>
  )
}

const dayTick = (iso: string) => `${iso.slice(8, 10)}.${iso.slice(5, 7)}`

/// «Аналітика» — «скільки заробив тренер, скільки компанія» (specs/services.md
/// §9): the month on the company's wall clock, four figures, a line per
/// provider and per service, the daily revenue. Every number opens the sale
/// lines it was summed from (SV7). A trainer (scope `own`) gets «Мій
/// заробіток»: their own line, no company figure.
function AnalyticsScreen({ ctx }: { ctx: ServicesContext }) {
  const { orgId } = ctx
  const a = t.services.analytics
  const search = Route.useSearch()
  const navigate = Route.useNavigate()
  const zone = ctx.zoneOf(null)
  const thisMonth = monthOf(wallOf(new Date(), zone).date)
  const month = search.month ?? thisMonth
  const branchId = search.branch && ctx.branches.some((b) => b.id === search.branch) ? search.branch : null
  const report = useQuery(earningsQuery(orgId, month, branchId, zone))
  const types = useQuery(classTypesQuery(orgId))
  const [drill, setDrill] = useState<DrillOpen | null>(null)

  const go = (next: Partial<Record<keyof Search, string | undefined>>) =>
    void navigate({
      search: (prev: Search) =>
        Object.fromEntries(Object.entries({ ...prev, ...next }).filter(([, value]) => value)) as Search,
      replace: true,
    })

  const toolbar = (
    <div className="flex flex-wrap items-center gap-2">
      <div className="flex items-center gap-1">
        <IconButton label={a.prevMonth} onClick={() => go({ month: addMonths(month, -1) })}>
          <ChevronLeft className="size-5" />
        </IconButton>
        <IconButton label={a.nextMonth} onClick={() => go({ month: addMonths(month, 1) })} disabled={month >= thisMonth}>
          <ChevronRight className="size-5" />
        </IconButton>
      </div>
      <h2 className="mr-auto px-1 text-callout font-semibold text-text first-letter:uppercase" aria-live="polite">
        {monthLabel(month)}
      </h2>
      {ctx.can.earnings === 'everyone' ? (
        <BranchSelect branches={ctx.branches} value={branchId} onChange={(id) => go({ branch: id ?? undefined })} all />
      ) : null}
    </div>
  )

  if (report.isPending) {
    return (
      <div className="flex flex-col gap-6">
        {toolbar}
        <LoadingBlock label={t.app.loading} />
      </div>
    )
  }
  if (report.isError) {
    return (
      <div className="flex flex-col gap-6">
        {toolbar}
        <InlineBanner kind="error" message={errorText(report.error)} action={{ label: t.app.retry, onClick: () => void report.refetch() }} />
      </div>
    )
  }

  const data = report.data
  const own = data.scope === 'own'
  const totals = data.totals
  const typeName = new Map((types.data ?? []).map((x) => [x.id, x.name]))
  const serviceName = (id: string | null) => (id ? (typeName.get(id) ?? t.services.unknownService) : t.services.unknownService)
  const open = (title: string, more: Partial<DrillOpen> = {}) => setDrill({ title, trainerUserId: null, ...more })

  const days = daysOfMonth(data.month)
  const byDay = new Map(data.byDay.map((day) => [day.date, day.revenueMinor]))
  const dayValues = days.map((day) => (byDay.get(day) ?? 0) / 100)
  const busiest = dayValues.indexOf(Math.max(0, ...dayValues))

  const providerColumns: readonly DataColumn<Earnings['providers'][number]>[] = [
    { key: 'name', header: a.columns.provider, width: 'minmax(0,1.4fr)', cell: (row) => ctx.nameOf(row.trainerUserId) },
    { key: 'sales', header: a.columns.sales, width: '88px', numeric: true, hideBelow: 'md', cell: (row) => row.sales },
    { key: 'revenue', header: a.columns.revenue, width: 'minmax(104px,0.8fr)', numeric: true, hideBelow: 'md', cell: (row) => formatMoney(row.revenueMinor) },
    {
      key: 'payout',
      header: a.columns.payout,
      width: 'minmax(104px,0.8fr)',
      numeric: true,
      mobile: 'trailing',
      cell: (row) => <span className="font-medium text-text">{formatMoney(row.payoutMinor)}</span>,
    },
    {
      key: 'company',
      header: a.columns.company,
      width: 'minmax(104px,0.8fr)',
      numeric: true,
      hideBelow: 'lg',
      cell: (row) => (row.companyMinor !== null ? formatMoney(row.companyMinor) : '—'),
    },
    {
      key: 'unpaid',
      header: a.columns.unpaid,
      width: 'minmax(104px,0.8fr)',
      numeric: true,
      hideBelow: 'lg',
      mobile: 'hidden',
      cell: (row) => <Unpaid minor={row.unpaidMinor} />,
    },
  ]

  const serviceColumns: readonly DataColumn<Earnings['byService'][number]>[] = [
    { key: 'name', header: a.columns.service, width: 'minmax(0,1.4fr)', cell: (row) => serviceName(row.classTypeId) },
    { key: 'sales', header: a.columns.sales, width: '88px', numeric: true, hideBelow: 'md', cell: (row) => row.sales },
    {
      key: 'revenue',
      header: a.columns.revenue,
      width: 'minmax(104px,0.8fr)',
      numeric: true,
      mobile: 'trailing',
      cell: (row) => <span className="font-medium text-text">{formatMoney(row.revenueMinor)}</span>,
    },
    { key: 'payout', header: own ? a.payoutOwn : a.columns.payout, width: 'minmax(104px,0.8fr)', numeric: true, hideBelow: 'md', cell: (row) => formatMoney(row.payoutMinor) },
    ...(own
      ? []
      : [
          {
            key: 'company',
            header: a.columns.company,
            width: 'minmax(104px,0.8fr)',
            numeric: true,
            hideBelow: 'lg',
            cell: (row: Earnings['byService'][number]) => (row.companyMinor !== null ? formatMoney(row.companyMinor) : '—'),
          } satisfies DataColumn<Earnings['byService'][number]>,
        ]),
    {
      key: 'unpaid',
      header: a.columns.unpaid,
      width: 'minmax(104px,0.8fr)',
      numeric: true,
      hideBelow: 'lg',
      mobile: 'hidden',
      cell: (row) => <Unpaid minor={row.unpaidMinor} />,
    },
  ]

  return (
    <div className={cn('flex flex-col gap-8', report.isPlaceholderData && 'opacity-60 transition-opacity')}>
      {toolbar}

      {data.accrualCount === 0 ? (
        <EmptyState icon={<ChartColumn />} title={a.empty} message={a.emptyMessage} />
      ) : (
        <>
          <dl className={cn('grid grid-cols-2 gap-3', own ? 'lg:grid-cols-3' : 'lg:grid-cols-4')}>
            {own ? (
              <Tile
                label={a.payoutOwn}
                value={formatMoney(totals.payoutMinor)}
                note={totals.payoutPlannedMinor > 0 ? a.payoutNote(formatMoney(totals.payoutPlannedMinor)) : undefined}
                onOpen={() => open(a.payoutOwn, { figure: 'payout' })}
              />
            ) : null}
            <Tile label={a.revenue} value={formatMoney(totals.revenueMinor)} note={a.revenueNote} onOpen={() => open(a.revenue, { figure: 'revenue' })} />
            {own ? null : (
              <Tile
                label={a.payout}
                value={formatMoney(totals.payoutMinor)}
                note={totals.payoutPlannedMinor > 0 ? a.payoutNote(formatMoney(totals.payoutPlannedMinor)) : undefined}
                onOpen={() => open(a.payout, { figure: 'payout' })}
              />
            )}
            {own || totals.companyMinor === null ? null : (
              <Tile label={a.company} value={formatMoney(totals.companyMinor)} note={a.companyNote} onOpen={() => open(a.company, { figure: 'company' })} />
            )}
            <Tile
              label={a.unpaid}
              value={formatMoney(totals.unpaidMinor)}
              note={totals.unpaidMinor > 0 ? a.unpaidNote : undefined}
              tone={totals.unpaidMinor > 0 ? 'warning' : 'normal'}
              onOpen={() => open(a.unpaid, { figure: 'unpaid' })}
            />
          </dl>
          <Notes figures={totals} block />

          {own ? null : (
            <Panel title={a.byProvider} count={data.providers.length}>
              <DataList
                label={a.byProvider}
                columns={providerColumns}
                rows={data.providers}
                rowKey={(row) => row.trainerUserId}
                rowTarget={(row) => ({ onClick: () => open(ctx.nameOf(row.trainerUserId), { trainerUserId: row.trainerUserId }) })}
                rowLabel={(row) => a.open(ctx.nameOf(row.trainerUserId))}
              />
            </Panel>
          )}

          <Panel title={a.byService} count={data.byService.length}>
            <DataList
              label={a.byService}
              columns={serviceColumns}
              rows={data.byService}
              rowKey={(row) => row.classTypeId ?? 'none'}
              rowTarget={(row) => ({ onClick: () => open(serviceName(row.classTypeId), { classTypeId: row.classTypeId }) })}
              rowLabel={(row) => a.open(serviceName(row.classTypeId))}
            />
          </Panel>

          <Panel title={a.byDay}>
            <div className="rounded-[16px] bg-surface px-4 pt-4 pb-2">
              {dayValues.some((value) => value > 0) ? (
                <ColumnChart
                  values={dayValues}
                  tickLabel={(index) => dayTick(days[index] ?? '')}
                  showTick={(index) => index % 5 === 0}
                  describe={(index) => a.dayLabel(formatDate(days[index] ?? ''), formatMoney((dayValues[index] ?? 0) * 100))}
                  summary={a.byDaySummary}
                  format={compactMoney}
                  mark={dayValues[busiest] ? busiest : null}
                  table={{
                    caption: a.byDay,
                    head: [a.tableDay, a.tableRevenue],
                    rows: days.map((day, index) => [formatDate(day), formatMoney((dayValues[index] ?? 0) * 100)] as const),
                  }}
                />
              ) : (
                <p className="pb-3 text-callout text-muted">{a.noRevenue}</p>
              )}
            </div>
          </Panel>
        </>
      )}

      <AccrualsPanel ctx={ctx} report={data} drill={drill} types={types.data ?? []} onClose={() => setDrill(null)} />
    </div>
  )
}

/// A figure that opens its rows — the shared tile (ui/stat-tile.tsx), its
/// button named the way «Аналітика» names a drill-down.
const Tile = ({
  label,
  value,
  note,
  tone = 'normal',
  onOpen,
}: {
  label: string
  value: string
  note?: string | undefined
  tone?: 'normal' | 'warning'
  onOpen: () => void
}) => (
  <StatTile label={label} value={value} note={note} tone={tone} onOpen={onOpen} openLabel={t.services.analytics.open(`${label}, ${value}`)} />
)

const Unpaid = ({ minor }: { minor: number }) =>
  minor > 0 ? <span className="text-warning">{formatMoney(minor)}</span> : <span className="text-muted">—</span>

/// What a number leaves out, in words: sales nothing priced, no-show pay
/// shown apart, pass-backed places.
const Notes = ({ figures, block = false }: { figures: Figures; block?: boolean }) => {
  const a = t.services.analytics
  const words = [
    figures.noRate > 0 ? a.noRate(figures.noRate) : null,
    figures.noShowPayoutMinor > 0 ? a.noShowPayout(formatMoney(figures.noShowPayoutMinor)) : null,
    figures.byPass > 0 ? a.byPass(figures.byPass) : null,
  ].filter(Boolean)
  if (words.length === 0) return null
  return block ? (
    <p className="-mt-5 px-1 text-caption text-muted">{words.join(' · ')}</p>
  ) : (
    <span className="text-caption text-muted" title={words.join(' · ')}>
      {words.join(' · ')}
    </span>
  )
}

const Panel = ({ title, count, children }: { title: string; count?: number | undefined; children: ReactNode }) => (
  <section className="flex flex-col gap-2">
    <SectionHeader title={title} count={count} />
    {children}
  </section>
)
